import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Canvas as FabricCanvas, Image as FabricImage } from 'fabric';

import { getTextureStyle } from '../data/texture';
import requestAIWithCache from '../utils/requestAIWithCache';
import { processDesignImages, loadImageWithCORS } from '../utils/imageUtils';
import { renderLayerOnFabricCanvas, applyPatternFill } from '../utils/renderLayer';
import { moveToIndex } from '../utils/imageUtils';
import { getGlobalResources } from '../utils/globalState';



// 隐藏线性缩放控制点
const hideLinearScaleControls = (o: any) => {
  try { o?.setControlsVisibility?.({ ml: false, mr: false, mt: false, mb: false }); } catch { }
};


interface ProductDesignRendererProps {
  design: any;
  previews: any;
  containerWidth?: number;
  containerHeight?: number;
  className?: string;
  hasResource?: boolean;
  index?: string | number;
  processedImage?: any[];
  optionValueId?: string;
}

const ProductDesignRenderer: React.FC<ProductDesignRendererProps> = ({
  design,
  optionValueId = "1",
  containerWidth = 400,
  containerHeight = 400,
  className = "",
  index = 0,
  hasResource = false,
  processedImage = [],
  previews
}) => {
  const designSignature = useMemo(() => JSON.stringify(design), [design]);
  const containerRef = useRef<HTMLDivElement>(null);
  const printAreaRef = useRef<HTMLDivElement>(null);
  const fabricCanvasRef = useRef<any>(null);
  const lastLayerSigsRef = useRef<string[]>([]);
  useEffect(() => {
    if (!printAreaRef.current) return;
    const canvasId = 'fabric-dynamic-canvas' + index;
    let canvasEl = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvasEl) {
      canvasEl = document.createElement('canvas');
      canvasEl.id = canvasId;
      canvasEl.style.width = '100%';
      canvasEl.style.height = '100%';
      canvasEl.style.position = 'absolute';
      canvasEl.style.top = '0';
      canvasEl.style.left = '0';
      printAreaRef.current.appendChild(canvasEl);
    }
    const fabricCanvas = new FabricCanvas(canvasEl.id, {
      width: printAreaRef.current.offsetWidth,
      height: printAreaRef.current.offsetHeight,
      preserveObjectStacking: true,
      backgroundColor: 'rgba(0,0,0,0)' // 设置画布背景透明
    });
    fabricCanvasRef.current = fabricCanvas;
    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      const el = document.getElementById(canvasId);
      if (el && printAreaRef.current && printAreaRef.current.contains(el)) {
        printAreaRef.current.removeChild(el);
      }
      lastLayerSigsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!fabricCanvasRef.current || !design?.faces?.[0]?.layers) return;
    const fabricCanvas = fabricCanvasRef.current;
    const layers = [...design.faces[0].layers];
    // 假如这个是平面，那么就开始层叠渲染
    const renderQueue = async () => {
      for (let idx = 0; idx < layers.length; idx++) {
        await renderLayerOnFabricCanvas(fabricCanvas, layers[idx], idx);
      }
    };

    if (design?.faces?.[0].obj || (
      Math.abs(design?.faces?.[0].rotation.z) > 0.05 ||
      Math.abs(design?.faces?.[0].rotation.y) > 0.05 ||
      Math.abs(design?.faces?.[0].rotation.x + 1.57) > 0.05
    )) {
      render3d();
    } else
      renderQueue();
  }, []);

  const render3d = () => {
    console.log("processDesignImages ...")
    processDesignImages('', { design: design } as any).then((r: any) => {
      if (r && r.length > 0) {
        const firstObj = r[0];
        if (firstObj && firstObj.image && Array.isArray(firstObj.image)) {
          const dataImage = firstObj.image.find((img: string) => img.startsWith('data:'));
          if (dataImage) {
            FabricImage.fromURL(dataImage, { crossOrigin: 'anonymous' } as any).then((img: any) => {
              if (img) {
                const canvas = fabricCanvasRef.current;
                const cw = canvas.getWidth();
                const ch = canvas.getHeight();
                img.set({
                  originX: 'center',
                  originY: 'center',
                  left: cw / 2,
                  top: ch / 2,
                });
                img.scaleToWidth(cw);
                if (img.getScaledHeight() < ch) {
                  img.scaleToHeight(ch);
                }
                canvas.add(img);
                canvas.renderAll();
              }
            });
          }
        }
      }
    })
  }
  useEffect(() => {
    if (!fabricCanvasRef.current || !design?.faces?.[0]?.layers) return;
    const fabricCanvas = fabricCanvasRef.current;
    const layers = [...design.faces[0].layers];
    if (lastLayerSigsRef.current.length !== layers.length) {
      try { fabricCanvas.clear(); } catch { }
      lastLayerSigsRef.current = [];
    }

    const processLayers = async () => {
      for (let idx = 0; idx < layers.length; idx++) {
        const layer = layers[idx];
        const sig = JSON.stringify(layer);
        if (lastLayerSigsRef.current[idx] !== sig) {
          let objects = fabricCanvas.getObjects();
          let obj = objects[idx] || null;
          if (obj && obj.type === 'i-text' && layer.text !== undefined) {
            obj.set({ text: layer.text });
          } else if (obj && obj.type === 'image' && layer.src) {
            let url = (layer.src || '') + '?x-oss-process=style/large';
            if (layer.ai) {
              try {
                let res = await requestAIWithCache(JSON.stringify({ code: layer.ai, url: layer.src }));
                if (res.status == "completed") {
                  url = (res.src || '') + '?x-oss-process=style/large';
                }
              } catch (e) { console.error(e) }
            }
            try { fabricCanvas.remove(obj); } catch { }
            (FabricImage as any).fromURL(url, { crossOrigin: 'anonymous' } as any).then((img: any) => {
              if (!img) return;
              img.set({
                left: obj.left,
                top: obj.top,
                originX: obj.originX,
                originY: obj.originY,
                scaleX: obj.scaleX,
                scaleY: obj.scaleY,
                angle: obj.angle,
                visible: obj.visible
              });
              moveToIndex(fabricCanvas, img, idx);
              fabricCanvas.renderAll();
            });
          } else if (obj && (obj.type === 'circle' || obj.type === 'rect' || obj.type === 'polygon')) {
            let url = (layer.fill.src || '') + '?x-oss-process=style/large';
            if (layer.ai) {
              try {
                let res = await requestAIWithCache(JSON.stringify({ code: layer.ai, url: layer.fill.src }));
                if (res.status == "completed") {
                  url = (res.src || '') + '?x-oss-process=style/large';
                }
              } catch (e) { console.error(e) }
            }
            applyPatternFill(obj, url).then(() => {
              fabricCanvas.requestRenderAll();
            });
          }
          fabricCanvas.requestRenderAll();
          lastLayerSigsRef.current[idx] = sig;
        }
      }
    }
    if (design?.faces?.[0].obj || (
      Math.abs(design?.faces?.[0].rotation.z) > 0.05 ||
      Math.abs(design?.faces?.[0].rotation.y) > 0.05 ||
      Math.abs(design?.faces?.[0].rotation.x + 1.57) > 0.05
    )) {
      render3d();
    } else {
      processLayers();
    }
  }, [designSignature]);
  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative bg-gray-100 overflow-hidden ${className}`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none'
      }}
    >
      {(previews && previews[0] && previews[0].type === "image") && (
        <div className="w-full h-full">
          <img
            src={previews[0].url}
            alt=""
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
        </div>
      )}
      {(previews && previews[0] && previews[0].type !== "image") && (
        <>
          {design.faces && design.faces[0] && design.faces[0].layers.length == 0 && (
            <div className="w-full h-full">
              <img
                src={previews[0].url} 
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
            </div>
          )}
          {design.faces && design.faces[0] && design.faces[0].layers.length > 0 && (
            <>
              {/* 渲染 design.covers 的第一张封面 */}
              {design.covers && design.covers.length > 0 && (
                <img
                  key={`cover-${design.covers[0].src}`}
                  // src={design.covers[optionValueId?.toString() || "1"].src}
                  src={design.covers.find((cover: any) => cover.option_value_id === optionValueId)?.src || design.covers[0].src}
                  alt=""
                  className="w-full h-full object-cover absolute inset-0"
                  style={{ zIndex: 202 }}
                  loading="lazy"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />
              )}

              {/* 图层组容器 - 使用 mask 限制显示范围 */}
              {design.faces && design.faces[0] && design.faces[0].layers && (

                <div
                  className="absolute"
                  style={{
                    left: `${(design.faces[0].print_area?.left || 0) * 100}%`,
                    top: `${(design.faces[0].print_area?.top || 0) * 100}%`,
                    width: `${(design.faces[0].print_area?.width || 1) * 100}%`,
                    height: `${(design.faces[0].print_area?.height || 1) * 100}%`,
                    zIndex: 200,
                    ...(design.faces[0].obj ? {} : {
                      WebkitMask: `url(https://www.giftmkr.com/giftfunc/view?code=${design.faces[0].code}) no-repeat center/contain`,
                      mask: `url(https://www.giftmkr.com/giftfunc/view?code=${design.faces[0].code}) no-repeat center/contain`,
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                    }),
                    minHeight: '1px',
                    minWidth: '1px'
                  }}
                  ref={printAreaRef}
                >
                  {/* 动态创建的canvas元素用于Fabric.js渲染 */}
                </div>
              )}

              {/* 渲染背景 - 限制在print_area内 */}
              {design.faces && design.faces[0] && design.faces[0].background && design.faces[0].print_area && (
                <div
                  className="absolute"
                  style={{
                    zIndex: 2,
                    left: `${(design.faces[0].print_area.left || 0) * 100}%`,
                    top: `${(design.faces[0].print_area.top || 0) * 100}%`,
                    width: `${(design.faces[0].print_area.width || 1) * 100}%`,
                    height: `${(design.faces[0].print_area.height || 1) * 100}%`,
                    backgroundColor: design.faces[0].background.color || 'transparent',
                    backgroundImage: design.faces[0].background.image ? `url(${design.faces[0].background.image})` : 'none',
                    backgroundSize: design.faces[0].background.image ? 'cover' : 'auto',
                    backgroundPosition: 'center',
                    backgroundRepeat: design.faces[0].background.image ? 'no-repeat' : 'repeat'
                  }}
                >
                  {design.faces[0].background.texture && (
                    <div
                      className="w-full h-full bg-repeat"
                      style={getTextureStyle(design.faces[0].background.texture)}
                    />
                  )}
                </div>
              )}

              {/* 渲染 design.backgrounds 的第一个背景 */}
              {design.backgrounds && design.backgrounds.length > 0 && (
                <img
                  key={`background-${design.backgrounds[0].src}`}
                  src={design.backgrounds.find((cover: any) => cover.option_value_id === optionValueId)?.src || design.backgrounds[0].src}
                  alt=""
                  className="w-full h-full object-cover absolute inset-0"
                  style={{ zIndex: 1 }}
                  loading="lazy"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ProductDesignRenderer;
